import {
  getNetCashBalance,
  getSignedLiabilityBalance,
} from "@lattice/core-lib/utils/portfolio-allocation";

export const ALL_PORTFOLIO_ACCOUNTS_ID = "all";

export const PORTFOLIO_HOME_INTERVALS = [
  "1D",
  "1W",
  "1M",
  "3M",
  "YTD",
  "1Y",
  "MAX",
] as const;

export type T_PortfolioHomeInterval = (typeof PORTFOLIO_HOME_INTERVALS)[number];

export type T_PortfolioHomeAccountOption = {
  description: string;
  id: string;
  label: string;
};

type T_PortfolioAssetLike = {
  cash_equivalent?: boolean | null;
  price: number | null;
  symbol: string;
  total_value?: number | null;
  units: number;
};

type T_PortfolioAccountLike<T_Asset extends T_PortfolioAssetLike> = {
  account_number: string;
  assets: T_Asset[];
  balance: number;
  cash_balance?: number;
  cuid: string;
  liability_balance?: number;
  nickname: string | null;
  snaptrade_user_cuid: string;
};

const CURRENCY_FORMATTER = new Intl.NumberFormat("en-US", {
  currency: "USD",
  currencyDisplay: "symbol",
  maximumFractionDigits: 2,
  minimumFractionDigits: 2,
  style: "currency",
});

export function isManualPortfolioAccount(account: {
  account_number: string;
  cuid: string;
  snaptrade_user_cuid: string;
}): boolean {
  return (
    account.snaptrade_user_cuid === "manual" ||
    account.account_number === "MANUAL" ||
    account.cuid.startsWith("manual:")
  );
}

export function isPortfolioCashLikeAsset(asset: T_PortfolioAssetLike): boolean {
  return (
    asset.cash_equivalent === true ||
    asset.symbol === "CASH" ||
    asset.symbol === "FCASH"
  );
}

export function getPortfolioAssetValue(asset: T_PortfolioAssetLike): number {
  if (asset.total_value !== null && asset.total_value !== undefined) {
    return asset.total_value;
  }

  if (asset.price !== null) return asset.price * asset.units;
  return 0;
}

export function getPortfolioAccountTotalValue<
  T_Asset extends T_PortfolioAssetLike,
  T_Account extends T_PortfolioAccountLike<T_Asset>,
>(account: T_Account): number {
  const holdingsValue = account.assets.reduce(
    (sum, asset) =>
      isPortfolioCashLikeAsset(asset)
        ? sum
        : sum + getPortfolioAssetValue(asset),
    0,
  );
  const cashBalance = account.cash_balance ?? Math.max(account.balance, 0);
  const liabilityBalance =
    account.liability_balance ??
    getSignedLiabilityBalance(Math.max(-account.balance, 0));

  return holdingsValue + getNetCashBalance({ cashBalance, liabilityBalance });
}

function getAccountLabel<T_Asset extends T_PortfolioAssetLike>(
  account: T_PortfolioAccountLike<T_Asset>,
): string {
  const nickname = account.nickname?.trim();
  if (nickname) return nickname;
  if (isManualPortfolioAccount(account)) return "Manual Holdings";

  const accountNumber = account.account_number.trim();
  return accountNumber ? `Account ${accountNumber}` : "Account";
}

function getAccountDescription<T_Asset extends T_PortfolioAssetLike>(
  account: T_PortfolioAccountLike<T_Asset>,
): string {
  const accountNumber = account.account_number.trim();
  const value = CURRENCY_FORMATTER.format(
    getPortfolioAccountTotalValue(account),
  );

  if (accountNumber && accountNumber !== "MANUAL") {
    return `Account ${accountNumber} · ${value}`;
  }

  return value;
}

export function buildPortfolioAccountOptions<
  T_Asset extends T_PortfolioAssetLike,
  T_Account extends T_PortfolioAccountLike<T_Asset>,
>(accounts: T_Account[]): T_PortfolioHomeAccountOption[] {
  return [
    {
      description: `${accounts.length} ${
        accounts.length === 1 ? "account" : "accounts"
      }`,
      id: ALL_PORTFOLIO_ACCOUNTS_ID,
      label: "All Accounts",
    },
    ...accounts.map((account) => ({
      description: getAccountDescription(account),
      id: account.cuid,
      label: getAccountLabel(account),
    })),
  ];
}

export function resolvePortfolioAccountSelection<
  T_Asset extends T_PortfolioAssetLike,
  T_Account extends T_PortfolioAccountLike<T_Asset>,
>(
  accounts: T_Account[],
  requestedAccountId: string,
): {
  isViewingAllAccounts: boolean;
  selectedAccountId: string;
  selectedAccounts: T_Account[];
} {
  const selectedAccount = accounts.find(
    (account) => account.cuid === requestedAccountId,
  );

  if (!selectedAccount) {
    return {
      isViewingAllAccounts: true,
      selectedAccountId: ALL_PORTFOLIO_ACCOUNTS_ID,
      selectedAccounts: accounts,
    };
  }

  return {
    isViewingAllAccounts: false,
    selectedAccountId: selectedAccount.cuid,
    selectedAccounts: [selectedAccount],
  };
}
